import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import aboutHeroBanner from "@/assets/about-hero-banner.png";
import { ShieldCheck, ThermometerSnowflake, MapPin, Wrench, Leaf, FileCheck, CheckCircle } from "lucide-react";

const credentials = [
  { icon: ShieldCheck, title: "ARC Licensed", desc: "Operating under ARC licence AU38134 — all refrigerant handling is carried out by accredited technicians as required by law." },
  { icon: ThermometerSnowflake, title: "Cool-Climate Specialists", desc: "We size and recommend systems specifically for sub-zero Highlands mornings, not generic capital-city conditions." },
  { icon: MapPin, title: "Based in Mittagong", desc: "Local to the region we service, so we understand elevation, frost pockets and exposure suburb by suburb." },
  { icon: Wrench, title: "Supply Through Aftercare", desc: "Assessment, quoting, installation, electrical commissioning and ongoing servicing — one accountable team." },
  { icon: Leaf, title: "Efficiency-Focused", desc: "We favour correctly sized, high-efficiency reverse cycle systems that cut running costs over a long heating season." },
  { icon: FileCheck, title: "Documented Workmanship", desc: "Written quotes, compliance certificates and workmanship cover provided with every installation." },
];

const values = [
  "Transparent, written quotes with no hidden costs",
  "Heating-first sizing, not floor-area guesswork",
  "Only genuine parts and manufacturer-backed warranties",
  "Clear scheduling with realistic installation timeframes",
  "Post-installation support and scheduled maintenance",
  "Honest advice — repair versus replace, sized correctly",
];

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="About ACG Southern Highlands | Cool-Climate Heating & Cooling Specialists"
        description="ACG Southern Highlands is based in Mittagong, ARC licensed (AU38134), and specialises in reverse cycle heating and cooling sized for frosty Highlands winters."
      />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "About Us" }]} />

      <div className="w-full">
        <img
          src={aboutHeroBanner}
          alt="ACG Southern Highlands technicians installing a reverse cycle air conditioning system in Mittagong"
          className="w-full h-auto object-cover"
          width={1920}
          height={480}
          loading="eager"
        />
      </div>

      {/* Hero */}
      <section className="bg-accent text-accent-foreground py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center space-y-4">
          <p className="text-primary font-semibold uppercase tracking-widest text-sm">Mittagong, NSW · ARC RTA AU38134</p>
          <h1 className="text-4xl md:text-5xl font-black leading-tight">
            About ACG Southern Highlands
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A locally based heating and cooling business that understands what a Southern Highlands winter actually
            demands of an air conditioning system — and installs accordingly.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 border-t-4 border-l-4 border-primary mt-1 flex-shrink-0" />
            <h2 className="text-3xl md:text-4xl font-black text-foreground">Our Story and Our Mittagong Base</h2>
          </div>

          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              ACG Southern Highlands is based in Mittagong, in the heart of the region we service. That matters more
              than it might first appear. A business headquartered in Sydney or on the coast is thinking about
              subtropical humidity and mild overnight minimums. We're thinking about frost on the outdoor unit at
              6am, roads that can ice over in Robertson or Bundanoon, and homes at 600 to 750 metres of elevation
              where a heater has to work every single day of winter, not just on the occasional cold snap.
            </p>
            <p>
              Being local also means we're not learning the terrain as we go. We know that a north-facing brick
              veneer in Bowral behaves very differently in July to a weatherboard cottage tucked into a valley near
              Berrima, and that a rural property outside Goulburn will see harder frosts and stronger winds than a
              town block in Mittagong itself. That local knowledge feeds directly into how we size, position and
              install every system we quote.
            </p>
            <p>
              Our approach is straightforward: recommend the system that will genuinely keep a home warm through the
              coldest Highlands mornings and comfortable through our short, mild summers, quote it honestly in
              writing, install it properly, and stand behind the workmanship afterwards. We'd rather lose a job on
              price than sell someone an undersized unit that struggles every winter.
            </p>
            <p>
              We work across homes and small commercial premises throughout Mittagong, Bowral, Moss Vale, Berrima,
              Burradoo, Bundanoon, Robertson, Goulburn, Picton, Tahmoor, Bargo, Thirlmere and the surrounding
              districts. See our <a href="/areas-we-service" className="text-primary underline underline-offset-2">areas we service</a> page
              for suburb-level detail, or head to <a href="/services" className="text-primary underline underline-offset-2">our services</a> page
              for the full range of installation, servicing and repair work we carry out.
            </p>
          </div>
        </div>
      </section>

      {/* Cool climate operating approach */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">How We Work in a Cool-Climate Region</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Most air conditioning marketing in Australia is written for hot, humid climates where cooling is the
              main job and heating is an afterthought. That framing doesn't hold up in the Southern Highlands. Here,
              a system spends more months of the year heating than cooling, and it needs to keep delivering warmth
              even when the outdoor temperature drops below zero.
            </p>
            <p>
              That changes several things about how we operate. We size systems on their heating capacity at low
              outdoor temperatures, not just their rated cooling output — a unit that looks adequate on a summer spec
              sheet can lose a significant chunk of its heating capacity once the mercury drops toward zero unless
              it carries a genuine cold-climate rating. We favour outdoor unit positions that avoid frost pockets and
              low-lying cold air, and we factor in wind exposure on open rural blocks around Robertson, Bundanoon and
              Goulburn.
            </p>
            <p>
              We also set expectations correctly around defrost cycles. A reverse cycle system working hard through
              a frosty morning will periodically pause to clear ice from its outdoor coil — that's normal operation,
              not a fault, and it's something we explain clearly at handover so it's never a surprise later.
            </p>
          </div>
        </div>
      </section>

      {/* Licensing & compliance */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Licensing and Compliance</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              ACG Southern Highlands operates under ARC licence <strong>ARC RTA AU38134</strong>. This licence,
              issued by the Australian Refrigeration Council, is a legal requirement for any business or individual
              handling refrigerant gases — covering the installation, servicing and safe decommissioning of air
              conditioning and refrigeration equipment. It exists to protect the environment from refrigerant
              leakage and to make sure the people working on your system genuinely know what they're doing.
            </p>
            <p>
              Refrigerant handling isn't a job for an unlicensed handyman or a general electrician. Our technicians
              are trained and accredited to correctly evacuate, charge, pressure-test and recover refrigerant, and
              to identify and repair leaks safely. Every refrigerant circuit we install or service is documented, and
              we keep records that can be produced if a warranty claim or compliance question ever arises.
            </p>
            <p>
              Electrical safety is treated with the same rigour. New circuits, isolators and wiring associated with
              your system are installed to Australian wiring standards, and every installation is tested and
              certified as part of commissioning before we consider the job complete. This protects your safety, your
              home insurance position, and your manufacturer warranty — most manufacturers will not honour a claim on
              a system that wasn't installed and commissioned correctly.
            </p>
          </div>
        </div>
      </section>

      {/* From quote to commissioning */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">What to Expect, From Quote to Commissioning</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We keep the path from first phone call to a working, commissioned system as clear as possible. It
              starts with a conversation about your home, your current heating and cooling situation, and what's not
              working. From there we arrange an on-site assessment, where we look at room sizes, ceiling height,
              insulation, glazing, orientation and how exposed the property is to frost and wind — all of which
              affect the real heat-load, not just the floor area.
            </p>
            <p>
              You then receive a written, itemised quote covering equipment, installation scope, electrical work and
              any structural considerations, with no surprise inclusions added later. Once you're ready to proceed we
              lock in a realistic installation date, and on the day our technicians carry out the mechanical,
              electrical and refrigerant work, test the system, and commission it to confirm it reaches its rated
              heating and cooling output. We walk you through operation before we leave, and provide compliance
              documentation and warranty paperwork as part of handover.
            </p>
            <p>
              For the full breakdown of each stage — including typical timelines, a site-assessment checklist and
              answers to common scheduling questions — see our <a href="/our-process" className="text-primary underline underline-offset-2">our process</a> page.
            </p>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Brands We Install and Why</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We install and service Daikin, Mitsubishi Electric, Fujitsu, Samsung, Panasonic and Actron Air. These
              brands were chosen deliberately rather than for convenience — each offers reverse cycle models with
              genuine cold-climate heating performance, meaning they maintain rated heating output at outdoor
              temperatures well below zero instead of losing capacity exactly when a Highlands home needs it most.
            </p>
            <p>
              The right brand and model for your home depends on your budget, the size and layout of the space, and
              the heating load calculated during your on-site assessment — there isn't a single "best" brand for
              every situation. We'll talk you through the realistic options for your property rather than steering
              you toward whatever happens to be in stock. Full model-level detail is on our <a href="/brands" className="text-primary underline underline-offset-2">brands</a> page.
            </p>
          </div>
        </div>
      </section>

      {/* Community / service area */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Our Community and Service Area</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We service homes and small businesses across Mittagong, Bowral, Moss Vale, Berrima, Burradoo,
              Bundanoon, Robertson, Goulburn, Picton, Tahmoor, Bargo and Thirlmere. Each of these towns has its own
              character when it comes to climate and housing stock — Robertson and Bundanoon sit higher and colder,
              Picton and Tahmoor bridge into the Wollondilly with slightly milder winters, and established towns like
              Bowral and Moss Vale mix heritage homes with newer developments, each needing a different installation
              approach.
            </p>
            <p>
              Because we're centrally based in Mittagong, travel to most of these towns is straightforward and
              factored transparently into your written quote — there are no hidden call-out surprises. Full
              suburb-by-suburb detail is available on our <a href="/areas-we-service" className="text-primary underline underline-offset-2">areas we service</a> page.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-black text-foreground text-center mb-10">Why Clients Trust ACG</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((c) => (
              <div key={c.title} className="bg-card rounded-lg p-6 space-y-3 shadow-sm">
                <c.icon className="w-8 h-8 text-primary" />
                <h3 className="font-bold text-foreground">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workmanship and warranty */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Workmanship and Our Warranty Approach</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Every system we sell carries the manufacturer's parts warranty, typically ranging from five to twelve
              years depending on brand and model. We explain those terms plainly at quoting stage, including what's
              required to keep the warranty valid — usually evidence of regular servicing by a licensed technician.
            </p>
            <p>
              On top of that, our installation workmanship is covered separately. If an issue arises from how the
              system was installed — rather than general wear, misuse, or a manufacturer parts fault — we rectify it
              at no charge within the workmanship warranty period. Terms are provided in writing with your quote and
              invoice, so you know exactly what's covered before work begins, and we keep service records on file in
              case a claim is ever needed down the track.
            </p>
          </div>
        </div>
      </section>

      {/* Environmental / efficiency */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Our Approach to Efficiency and the Environment</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              A correctly sized, high-efficiency reverse cycle system is one of the more environmentally sensible
              heating choices available for a cool-climate home — it moves heat rather than generating it by burning
              fuel, delivering several times more heating energy than the electricity it consumes. We recommend
              inverter systems wherever appropriate, because they modulate output to match demand instead of cycling
              fully on and off, using less energy over a long Highlands heating season and wearing components more
              gently.
            </p>
            <p>
              We're also careful with refrigerant handling for environmental reasons, not only compliance ones.
              Refrigerants are potent greenhouse gases if released to atmosphere, so our ARC-licensed technicians
              recover and handle refrigerant correctly during every installation, service and decommission, and we
              repair leaks properly rather than simply topping up a system that's losing gas.
            </p>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-16 px-4 bg-accent text-accent-foreground">
        <div className="container mx-auto max-w-4xl space-y-8">
          <h2 className="text-3xl font-black">Our Commitment to You</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {values.map((v) => (
              <div key={v} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Whether you need a single split system for a cold bedroom or a full ducted install for a Highlands home,
            we apply the same standard: the right system, correctly sized for our climate, professionally installed,
            and backed by real after-sales support. Ready to get started?{" "}
            <a href="/contact-us" className="text-primary underline underline-offset-2">Get in touch</a> or
            explore <a href="/services" className="text-primary underline underline-offset-2">our services</a>.
          </p>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default About;
